/**
 * Generic manifest CONDITION matching — the shared engine behind field/section visibility
 * (`display-options.ts` showIf/hideIf) AND any other declarative gate (e.g. `manifest.skip`).
 * Not display-specific: it just evaluates a `DisplayCondition` (the condition shape) against a
 * value context.
 *
 * A condition is a flat map of field-ref → candidate-values, plus two reserved keys:
 *   - `whenSet: string[]`   — listed refs must be SET   (`isSet` semantics: null/undefined/''/[]
 *                             count as unset; 0/false count as set).
 *   - `whenUnset: string[]` — listed refs must be UNSET (inverse of `whenSet`).
 *
 * Clause polarity:
 *   - `evaluateShowIfClause` — AND across value-equality + every whenSet set + every whenUnset unset.
 *   - `evaluateHideIfClause` — OR  across the same (any one satisfied → clause matches).
 *
 * Refs may be:
 *  - bare/dotted form keys (`mode`, `meta.tune`, `profile.userId`) — resolved against the values
 *  - prefixed external sources (`feature.X`, `account.X`, `system.X`) — resolved against `company`
 */
import type { CompanyInfo, DisplayCondition } from '@scio3/shared-types';
import { getNested } from './nested-keys';
import { isExternalKey, resolveCompanyKey } from './company-resolver';
import { isSet } from '../hooks/useFieldInteraction';

function resolveKey(
  key: string,
  values: Record<string, unknown>,
  company: CompanyInfo | null | undefined,
): unknown {
  if (isExternalKey(key)) {
    return resolveCompanyKey(key, company);
  }
  return getNested(values, key);
}

function isEmptyPlainObject(v: unknown): v is Record<string, never> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0;
}

function matchValue(candidate: unknown, resolved: unknown): boolean {
  if (isEmptyPlainObject(candidate)) return isEmptyPlainObject(resolved);
  return candidate === resolved;
}

/** Peel reserved `whenSet` + `whenUnset` keys out of a DisplayCondition.
 *  Returns the lists (or undefined) plus the value-equality map with reserved keys removed. */
function splitCondition(cond: DisplayCondition): {
  whenSet: string[] | undefined;
  whenUnset: string[] | undefined;
  whenMap: Record<string, unknown[]>;
} {
  const { whenSet, whenUnset, ...rest } = cond;
  return {
    whenSet: Array.isArray(whenSet) ? (whenSet as string[]) : undefined,
    whenUnset: Array.isArray(whenUnset) ? (whenUnset as string[]) : undefined,
    whenMap: rest as Record<string, unknown[]>,
  };
}

/** Helper: build the predicate for a `whenSet` / `whenUnset` ref against the value context.
 *  Returns `true` when the ref's state matches the requested mode
 *  (`'set'` → key isSet; `'unset'` → key NOT isSet). */
function matchSetRef(
  ref: string,
  mode: 'set' | 'unset',
  values: Record<string, unknown>,
  company: CompanyInfo | null | undefined,
): boolean {
  const set = isSet(resolveKey(ref, values, company));
  return mode === 'set' ? set : !set;
}

/** Normalize the legacy single-clause `DisplayCondition` and the new `DisplayCondition[]` array
 *  form into a clause array for uniform iteration. Exported so the validator (and any other
 *  consumer) can match the engine's unwrap semantics 1:1. */
export function toClauseArray<T extends DisplayCondition>(cond: T | T[]): T[] {
  return Array.isArray(cond) ? cond : [cond];
}

/** Evaluate a single showIf-style clause: AND across value-equality + every whenSet entry set +
 *  every whenUnset entry unset. Returns `true` when the clause is satisfied. */
export function evaluateShowIfClause(
  cond: DisplayCondition,
  values: Record<string, unknown>,
  company: CompanyInfo | null | undefined,
): boolean {
  const { whenSet, whenUnset, whenMap } = splitCondition(cond);
  const valueOk = Object.entries(whenMap).every(([key, vals]) => {
    const resolved = resolveKey(key, values, company);
    return (vals ?? []).some((v) => matchValue(v, resolved));
  });
  if (!valueOk) return false;
  if (whenSet?.length && !whenSet.every((k) => matchSetRef(k, 'set', values, company))) {
    return false;
  }
  if (whenUnset?.length && !whenUnset.every((k) => matchSetRef(k, 'unset', values, company))) {
    return false;
  }
  return true;
}

/** Evaluate a single hideIf-style clause: OR across value-equality + any whenSet entry set +
 *  any whenUnset entry unset. Returns `true` when the clause is satisfied. */
export function evaluateHideIfClause(
  cond: DisplayCondition,
  values: Record<string, unknown>,
  company: CompanyInfo | null | undefined,
): boolean {
  const { whenSet, whenUnset, whenMap } = splitCondition(cond);
  const valueHide = Object.entries(whenMap).some(([key, vals]) => {
    const resolved = resolveKey(key, values, company);
    return (vals ?? []).some((v) => matchValue(v, resolved));
  });
  if (valueHide) return true;
  if (whenSet?.length && whenSet.some((k) => matchSetRef(k, 'set', values, company))) {
    return true;
  }
  if (whenUnset?.length && whenUnset.some((k) => matchSetRef(k, 'unset', values, company))) {
    return true;
  }
  return false;
}

/** Evaluate a standalone gate condition against a value context. Returns true when ANY clause
 *  matches (OR-of-clauses, each clause OR-of-keys — the hideIf "trigger" polarity). Used by
 *  `manifest.skip` rules to decide whether to SKIP an api call — e.g. skip `onLoad` with
 *  `{ whenUnset: ['assetId'] }` on create (where `assetId` is resolved from `values`). */
export function matchesCondition(
  cond: DisplayCondition | DisplayCondition[],
  values: Record<string, unknown>,
  company?: CompanyInfo | null,
): boolean {
  return toClauseArray(cond).some((c) => evaluateHideIfClause(c, values, company));
}
